import {
  Divider,
  StyledMenu,
  StyledMenuItem,
  StyledIconButton,
  StyledListItemIcon,
  StyledListItemText,
  UnavailableWarning,
  UnavailableIconButton,
} from './ProductCellMoreMenu.styles.ts'
import ReplaceIcon from '@/core/assets/icons/replace.svg'
import RemoveIcon from '@/core/assets/icons/remove.svg'
import { bindTrigger, bindMenu } from 'material-ui-popup-state'
import MoreIcon from '@/core/assets/icons/more.svg'
import UnavailableInfoIcon from '@/core/assets/icons/unavailable-info.svg'
import { usePopupState } from 'material-ui-popup-state/hooks'
import { useRemoveBranchesSalableProductMutation } from '@/core/api/graphql/forshawGraphqlApi.generated.ts'
import { FC } from 'react'
import { useAccountBranchIdsParams } from '@/core/hooks/useAccountBranchIdsParams.ts'
import { replenishmentPlanActions } from '@/data/replenishmentPlan/slice/replenishmentPlan.slice.ts'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { useSearchProducts } from '@/data/replenishmentPlan/hooks/useSearchProducts.ts'
import { selectSearch } from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import { useTrackReplenishmentPlanAnalyticsEvent } from '@/core/hooks/useTrackReplenishmentPlanAnalyticsEvent.ts'
import {
  DatagridActionsEvent,
  DatagridActionsType,
} from '@/core/analytics/events/replenishmentPlan/DatagridActionsEvent.ts'
import { Tooltip } from '@mui/material'

interface Props {
  product: Product
}

const ProductCellMoreMenu: FC<Props> = ({ product }) => {
  const { branchId } = useAccountBranchIdsParams()

  const dispatch = useDispatch()

  const [removeBranchesSalableProduct] =
    useRemoveBranchesSalableProductMutation()

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'productMoreMenu',
  })

  const { searchProducts } = useSearchProducts()

  const { trackReplenishmentPlanAnalyticsEvent } =
    useTrackReplenishmentPlanAnalyticsEvent()

  const search = useSelector(selectSearch)

  const onRemove = async () => {
    trackReplenishmentPlanAnalyticsEvent(
      new DatagridActionsEvent({ type: DatagridActionsType.RemoveFromPlan }),
    )

    popupState.close()

    if (product.salableProductId == null) {
      throw new Error(
        `Cannot remove product ${product.id} from the plan: no salableProductId`,
      )
    }

    await removeBranchesSalableProduct({
      branchId,
      salableProductId: product.salableProductId,
      productId: product.id,
    })

    // Do not search if there is no search query
    if (search) {
      await searchProducts(search)
    }

    // Notify the host app to refresh cart data (same window now that the plan is inline)
    window.postMessage({ type: 'CART_INVALIDATED' }, '*')
  }

  const onReplace = () => {
    trackReplenishmentPlanAnalyticsEvent(
      new DatagridActionsEvent({ type: DatagridActionsType.ReplaceIntent }),
    )

    popupState.close()

    dispatch(replenishmentPlanActions.setReplaceProductId(product.id))
  }

  const { t } = useTranslation()

  return (
    <>
      {product.unavailable ? (
        <Tooltip
          title={t('productCellMoreMenu.unavailableTooltip')}
          placement="top"
        >
          <UnavailableIconButton size="small" {...bindTrigger(popupState)}>
            <UnavailableInfoIcon />
          </UnavailableIconButton>
        </Tooltip>
      ) : (
        <StyledIconButton size="small" {...bindTrigger(popupState)}>
          <MoreIcon />
        </StyledIconButton>
      )}
      <StyledMenu {...bindMenu(popupState)}>
        {product.unavailable && (
          <>
            <UnavailableWarning>
              {t('productCellMoreMenu.unavailableWarning')}
            </UnavailableWarning>
            <Divider />
          </>
        )}
        <StyledMenuItem onClick={onReplace}>
          <StyledListItemIcon>
            <ReplaceIcon />
          </StyledListItemIcon>
          <StyledListItemText>
            {t('productCellMoreMenu.replace')}
          </StyledListItemText>
        </StyledMenuItem>
        <Divider />
        <StyledMenuItem onClick={onRemove}>
          <StyledListItemIcon>
            <RemoveIcon />
          </StyledListItemIcon>
          <StyledListItemText>
            {t('productCellMoreMenu.remove')}
          </StyledListItemText>
        </StyledMenuItem>
      </StyledMenu>
    </>
  )
}

export default ProductCellMoreMenu
